import { useLayoutEffect } from 'react';
import { keepElementInViewport } from '../lessonPlayer/services/ViewportPositionService';

/**
 * Keeps `ref.current` inside the visible viewport while `enabled` is true.
 * Re-runs when `deps` change (e.g. open state, loaded content size).
 *
 * @param {React.RefObject} ref - element to keep on-screen
 * @param {boolean} enabled
 * @param {any[]} deps
 * @param {{ padding?: number, preferTransform?: boolean, anchorRef?: React.RefObject, gap?: number }} options
 */
const useKeepInViewport = (ref, enabled, deps = [], options = {}) => {
  const {
    padding,
    preferTransform = false,
    anchorRef,
    gap,
  } = options;

  useLayoutEffect(() => {
    if (!enabled) {
      if (ref?.current && preferTransform) {
        ref.current.style.transform = '';
      }
      return undefined;
    }

    const el = ref?.current;
    if (!el) {
      return undefined;
    }

    const run = () => keepElementInViewport(el, {
      padding,
      preferTransform,
      gap,
      anchorEl: anchorRef?.current ?? null,
    });

    run();
    // Second pass after fonts/images/layout settle
    const rafId = window.requestAnimationFrame(run);

    window.addEventListener('resize', run);
    window.addEventListener('scroll', run, true);
    window.visualViewport?.addEventListener('resize', run);
    window.visualViewport?.addEventListener('scroll', run);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', run);
      window.removeEventListener('scroll', run, true);
      window.visualViewport?.removeEventListener('resize', run);
      window.visualViewport?.removeEventListener('scroll', run);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies deps explicitly
  }, [enabled, preferTransform, padding, gap, ...deps]);
};

export default useKeepInViewport;
